/**
 * Leviathan Web - Delivery Report Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Delivery metrics by driver, truck, and product
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  RefreshCw,
  Printer,
  Truck,
  Users,
  Package,
  TrendingUp,
  CheckCircle,
  Fuel,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface DeliveryReport {
  dateRange: { startDate: string; endDate: string };
  generatedAt: string;
  summary: {
    totalDeliveries: number;
    completedDeliveries: number;
    completionRate: number;
    totalGallons: number;
    averageGallonsPerDelivery: number;
  };
  byDriver: Array<{
    driverId: string;
    name: string;
    deliveries: number;
    gallons: number;
  }>;
  byTruck: Array<{
    truckId: string;
    unitNumber: string;
    deliveries: number;
    gallons: number;
  }>;
  byProduct: Array<{
    productName: string;
    gallons: number;
    deliveries: number;
  }>;
}

/**
 * Stat card component
 */
function StatCard({
  label,
  value,
  icon,
  status = 'neutral',
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  status?: 'neutral' | 'operational' | 'warning' | 'critical';
}) {
  const statusColors = {
    neutral: 'border-panel-border text-amber',
    operational: 'border-success-green text-success-green',
    warning: 'border-warning-amber text-warning-amber',
    critical: 'border-error-red text-error-red',
  };

  return (
    <div
      className={`relative bg-panel-dark border ${statusColors[status].split(' ')[0]} p-4`}
    >
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="flex items-center space-x-2 mb-2">
        <span className={`${statusColors[status].split(' ')[1]}`}>{icon}</span>
        <span className="text-text-tertiary text-xs uppercase tracking-widest font-mono">
          {label}
        </span>
      </div>
      <div
        className={`text-xl font-mono font-bold ${statusColors[status].split(' ')[1]}`}
        style={{ textShadow: '0 0 10px currentColor' }}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Delivery Report Page
 */
const DeliveryReport = () => {
  const { accessToken } = useAuth();
  const [report, setReport] = useState<DeliveryReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'summary' | 'drivers' | 'trucks' | 'products'>(
    'summary'
  );

  // Default to current month
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split('T')[0] || '');
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0] || '');

  useEffect(() => {
    fetchReport();
  }, [startDate, endDate]);

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reports/delivery?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setReport(data.report);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setQuickRange = (range: string) => {
    const now = new Date();
    let start: Date;
    const end: Date = now;

    switch (range) {
      case 'today':
        start = now;
        break;
      case 'week':
        start = new Date(now);
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        break;
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0] || '');
    setEndDate(end.toISOString().split('T')[0] || '');
  };

  const getCompletionStatus = (rate: number): 'operational' | 'warning' | 'critical' => {
    if (rate >= 95) return 'operational';
    if (rate >= 80) return 'warning';
    return 'critical';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/reports"
            className="p-2 text-text-tertiary hover:text-amber transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-text-primary font-mono uppercase tracking-wider">
              Delivery Report
            </h1>
            <p className="text-sm text-text-secondary font-mono mt-1">
              Delivery metrics by driver, truck, and product
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => window.print()}
            className="flex items-center space-x-2 px-4 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors"
          >
            <Printer className="w-4 h-4" />
            <span className="font-mono text-sm">Print</span>
          </button>
        </div>
      </div>

      {/* Date Controls */}
      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-background border border-panel-border focus:border-amber px-4 py-2 text-text-primary font-mono focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-background border border-panel-border focus:border-amber px-4 py-2 text-text-primary font-mono focus:outline-none"
            />
          </div>
          <div className="flex gap-2">
            {['today', 'week', 'month', 'quarter'].map((range) => (
              <button
                key={range}
                onClick={() => setQuickRange(range)}
                className="bg-background border border-panel-border hover:border-amber/50 px-3 py-2 text-text-secondary hover:text-amber font-mono text-xs uppercase transition-colors"
              >
                {range}
              </button>
            ))}
          </div>
          <button
            onClick={fetchReport}
            disabled={isLoading}
            className="flex items-center space-x-2 bg-amber text-background px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-amber/80 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            label="Total Deliveries"
            value={report.summary.totalDeliveries.toString()}
            icon={<Truck className="w-5 h-5" />}
          />
          <StatCard
            label="Completed"
            value={report.summary.completedDeliveries.toString()}
            icon={<CheckCircle className="w-5 h-5" />}
            status="operational"
          />
          <StatCard
            label="Completion Rate"
            value={`${report.summary.completionRate.toFixed(1)}%`}
            icon={<TrendingUp className="w-5 h-5" />}
            status={getCompletionStatus(report.summary.completionRate)}
          />
          <StatCard
            label="Total Gallons"
            value={report.summary.totalGallons.toLocaleString()}
            icon={<Fuel className="w-5 h-5" />}
          />
          <StatCard
            label="Avg per Delivery"
            value={`${report.summary.averageGallonsPerDelivery.toLocaleString()} gal`}
            icon={<Package className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-panel-border">
        {[
          { id: 'summary', label: 'Summary', icon: <TrendingUp className="w-4 h-4" /> },
          { id: 'drivers', label: 'By Driver', icon: <Users className="w-4 h-4" /> },
          { id: 'trucks', label: 'By Truck', icon: <Truck className="w-4 h-4" /> },
          { id: 'products', label: 'By Product', icon: <Package className="w-4 h-4" /> },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`flex items-center space-x-2 px-4 py-3 font-mono text-sm uppercase transition-colors ${
              activeTab === tab.id
                ? 'text-amber border-b-2 border-amber'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6">
          {isLoading ? (
            <div className="text-center py-12 text-text-tertiary font-mono">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading report...
            </div>
          ) : !report ? (
            <div className="text-center py-12 text-text-tertiary font-mono">
              No data available
            </div>
          ) : (
            <>
              {/* Summary Tab */}
              {activeTab === 'summary' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-mono text-text-primary uppercase tracking-wide mb-4 pb-2 border-b border-amber">
                      Top Drivers
                    </h3>
                    <div className="space-y-2">
                      {report.byDriver.slice(0, 5).map((driver, idx) => (
                        <div
                          key={driver.driverId}
                          className="flex justify-between items-center p-3 bg-background border border-panel-border"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-amber font-mono font-bold">#{idx + 1}</span>
                            <Link
                              to={`/drivers/${driver.driverId}`}
                              className="text-text-primary hover:text-amber font-mono"
                            >
                              {driver.name}
                            </Link>
                          </div>
                          <div className="text-right">
                            <div className="text-amber font-mono font-semibold">
                              {driver.gallons.toLocaleString()} gal
                            </div>
                            <div className="text-text-tertiary font-mono text-xs">
                              {driver.deliveries} deliveries
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-mono text-text-primary uppercase tracking-wide mb-4 pb-2 border-b border-amber">
                      Top Products
                    </h3>
                    <div className="space-y-2">
                      {report.byProduct.slice(0, 5).map((product, idx) => (
                        <div
                          key={product.productName}
                          className="flex justify-between items-center p-3 bg-background border border-panel-border"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-amber font-mono font-bold">#{idx + 1}</span>
                            <span className="text-text-primary font-mono">{product.productName}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-amber font-mono font-semibold">
                              {product.gallons.toLocaleString()} gal
                            </div>
                            <div className="text-text-tertiary font-mono text-xs">
                              {product.deliveries} deliveries
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Drivers Tab */}
              {activeTab === 'drivers' && (
                <table className="w-full">
                  <thead className="border-b border-panel-border">
                    <tr>
                      <th className="text-left px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Rank
                      </th>
                      <th className="text-left px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Driver
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Deliveries
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Gallons
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Avg/Delivery
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byDriver.map((driver, idx) => (
                      <tr
                        key={driver.driverId}
                        className="border-b border-panel-border/50 hover:bg-amber/5"
                      >
                        <td className="px-4 py-3 text-amber font-mono font-bold">#{idx + 1}</td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/drivers/${driver.driverId}`}
                            className="text-amber hover:text-amber/80 font-mono"
                          >
                            {driver.name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-right text-text-secondary font-mono">
                          {driver.deliveries}
                        </td>
                        <td className="px-4 py-3 text-right text-amber font-mono font-semibold">
                          {driver.gallons.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-text-secondary font-mono">
                          {driver.deliveries > 0
                            ? Math.round(driver.gallons / driver.deliveries).toLocaleString()
                            : 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Trucks Tab */}
              {activeTab === 'trucks' && (
                <table className="w-full">
                  <thead className="border-b border-panel-border">
                    <tr>
                      <th className="text-left px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Rank
                      </th>
                      <th className="text-left px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Truck
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Deliveries
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Gallons
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Avg/Delivery
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byTruck.map((truck, idx) => (
                      <tr
                        key={truck.truckId}
                        className="border-b border-panel-border/50 hover:bg-amber/5"
                      >
                        <td className="px-4 py-3 text-amber font-mono font-bold">#{idx + 1}</td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/trucks/${truck.truckId}`}
                            className="text-steel hover:text-steel/80 font-mono flex items-center space-x-2"
                          >
                            <Truck className="w-4 h-4" />
                            <span>{truck.unitNumber}</span>
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-right text-text-secondary font-mono">
                          {truck.deliveries}
                        </td>
                        <td className="px-4 py-3 text-right text-amber font-mono font-semibold">
                          {truck.gallons.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-text-secondary font-mono">
                          {truck.deliveries > 0
                            ? Math.round(truck.gallons / truck.deliveries).toLocaleString()
                            : 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Products Tab */}
              {activeTab === 'products' && (
                <table className="w-full">
                  <thead className="border-b border-panel-border">
                    <tr>
                      <th className="text-left px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Rank
                      </th>
                      <th className="text-left px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Product
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Deliveries
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        Gallons
                      </th>
                      <th className="text-right px-4 py-2 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                        % of Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byProduct.map((product, idx) => (
                      <tr
                        key={product.productName}
                        className="border-b border-panel-border/50 hover:bg-amber/5"
                      >
                        <td className="px-4 py-3 text-amber font-mono font-bold">#{idx + 1}</td>
                        <td className="px-4 py-3 text-text-primary font-mono">
                          {product.productName}
                        </td>
                        <td className="px-4 py-3 text-right text-text-secondary font-mono">
                          {product.deliveries}
                        </td>
                        <td className="px-4 py-3 text-right text-amber font-mono font-semibold">
                          {product.gallons.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-text-secondary font-mono">
                          {report.summary.totalGallons > 0
                            ? ((product.gallons / report.summary.totalGallons) * 100).toFixed(1)
                            : 0}
                          %
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      {/* Report Info */}
      {report && (
        <div className="text-center text-text-tertiary font-mono text-xs">
          Report generated: {new Date(report.generatedAt).toLocaleString()} | Period:{' '}
          {new Date(report.dateRange.startDate).toLocaleDateString()} -{' '}
          {new Date(report.dateRange.endDate).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default DeliveryReport;
